import React from 'react';
import { TrendingUp, ArrowRight, Check, Quote, ArrowUpRight } from 'lucide-react';
import Reveal from './Reveal';
import { NUOLA_CASE, VERIFIED_RESULTS, CAMPAIGN_PROOF, RESULT_ASSETS, NUOLA_SLACK } from '../mock';

function FeaturedCase() {
  const c = NUOLA_CASE;
  return (
    <div className="rounded-[2rem] border border-[var(--ob-line)] bg-white p-6 md:p-10 lg:p-14">
      {/* Header */}
      <Reveal>
        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-[var(--ob-accent)] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[var(--ob-dark)]">
            Featured Case Study
          </span>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--ob-ink-soft)]">
            <span className="h-2 w-2 rounded-full bg-[var(--ob-accent)]" /> Verified Klaviyo Data
          </span>
        </div>
        <h3 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-[var(--ob-ink)] md:text-6xl">
          {c.brand}
        </h3>
        <p className="mt-2 text-sm font-semibold uppercase tracking-[0.14em] text-[var(--ob-ink-soft)]">
          {c.tag} · {c.kicker}
        </p>
        <p className="mt-6 max-w-3xl text-lg leading-relaxed text-[var(--ob-ink-soft)]">{c.intro}</p>
      </Reveal>

      {/* Big verified before/after visual */}
      <Reveal delay={80}>
        <figure className="ob-email-card mt-10 overflow-hidden rounded-2xl border border-[var(--ob-line)] bg-white">
          <img
            src={RESULT_ASSETS.nuola}
            alt="Nuola Wigs verified Klaviyo before and after dashboard"
            loading="lazy"
            className="block w-full"
          />
        </figure>
      </Reveal>

      {/* Problem + Results */}
      <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
        {/* Problem */}
        <Reveal>
          <h4 className="font-display text-xl font-bold text-[var(--ob-ink)]">The problem when we started</h4>
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {c.problem.map((p) => (
              <div key={p.label} className="rounded-2xl bg-[var(--ob-bg-alt)] p-5">
                <div className="font-display text-2xl font-extrabold text-[var(--ob-ink)]">{p.stat}</div>
                <div className="mt-1.5 text-sm leading-snug text-[var(--ob-ink-soft)]">{p.label}</div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Results table */}
        <Reveal delay={100}>
          <h4 className="font-display text-xl font-bold text-[var(--ob-ink)]">The result in a single month</h4>
          <div className="mt-5 overflow-hidden rounded-2xl border border-[var(--ob-line)]">
            <div className="grid grid-cols-[1.4fr_1fr_1fr_0.9fr] bg-[var(--ob-ink)] px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-white/70">
              <span>Metric</span>
              <span className="text-right">Before</span>
              <span className="text-right">After</span>
              <span className="text-right">Change</span>
            </div>
            {c.metrics.map((m, i) => (
              <div
                key={m.label}
                className={`grid grid-cols-[1.4fr_1fr_1fr_0.9fr] items-center px-4 py-3 text-sm ${
                  i % 2 ? 'bg-[var(--ob-bg-alt)]' : 'bg-white'
                }`}
              >
                <span className="font-semibold text-[var(--ob-ink)]">{m.label}</span>
                <span className="text-right text-[var(--ob-ink-soft)]">{m.before}</span>
                <span className="text-right font-semibold text-[var(--ob-ink)]">{m.after}</span>
                <span className="text-right font-display font-bold text-[var(--ob-accent-dark)]">{m.change}</span>
              </div>
            ))}
          </div>

          {/* Deliverability */}
          <div className="mt-5 grid grid-cols-3 gap-3">
            {c.deliverability.map((d) => (
              <div key={d.label} className="rounded-2xl border border-[var(--ob-line)] p-4 text-center">
                <div className="font-display text-2xl font-extrabold text-[var(--ob-accent-dark)]">{d.value}</div>
                <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-[var(--ob-ink)]">{d.label}</div>
                <div className="mt-1 text-[11px] leading-snug text-[var(--ob-ink-soft)]">{d.note}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      {/* What we did */}
      <Reveal className="mt-12">
        <h4 className="font-display text-xl font-bold text-[var(--ob-ink)]">What we did</h4>
        <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 md:grid-cols-2">
          {c.work.map((w) => (
            <div key={w} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--ob-accent)]/20 text-[var(--ob-accent-dark)]">
                <Check size={14} />
              </span>
              <span className="text-[15px] leading-snug text-[var(--ob-ink-soft)]">{w}</span>
            </div>
          ))}
        </div>
        <div className="mt-6 rounded-2xl border-l-4 border-[var(--ob-accent)] bg-[var(--ob-bg-alt)] p-5 text-[var(--ob-ink)]">
          <span className="font-semibold">The key insight: </span>
          <span className="text-[var(--ob-ink-soft)]">{c.insight}</span>
        </div>
      </Reveal>

      {/* Real client Slack proof */}
      <Reveal className="mt-10">
        <div className="grid grid-cols-1 gap-6 rounded-2xl bg-[var(--ob-ink)] p-6 md:grid-cols-[0.9fr_1.1fr] md:items-center md:gap-8 md:p-8">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[var(--ob-accent)]">
              <Quote size={14} /> Straight From The Client
            </div>
            <p className="mt-4 font-display text-2xl font-bold leading-snug text-white md:text-3xl">
              “Yeah amazing stuff! Should have been doing this years ago.”
            </p>
            <p className="mt-3 text-sm text-white/60">{c.quoteBy}</p>
          </div>
          <figure className="overflow-hidden rounded-2xl border border-white/10 bg-[var(--ob-dark)]">
            <img
              src={NUOLA_SLACK}
              alt="Real Slack message from the Nuola Wigs client sharing their results"
              loading="lazy"
              className="block w-full"
            />
          </figure>
        </div>
      </Reveal>
    </div>
  );
}

function ResultBlock({ item, index }) {
  return (
    <Reveal delay={index * 60}>
      <article className="grid grid-cols-1 gap-6 rounded-3xl border border-[var(--ob-line)] bg-white p-5 md:p-8 lg:grid-cols-[1fr_1.6fr] lg:items-center lg:gap-10">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-[var(--ob-accent)]/15 px-4 py-2 text-[var(--ob-accent-dark)]">
            <TrendingUp size={18} />
            <span className="font-display text-lg font-bold">{item.highlight}</span>
          </div>
          <h3 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-[var(--ob-ink)]">
            {item.brand}
          </h3>
          <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-[var(--ob-ink-soft)]">{item.tag}</p>
          <p className="mt-4 text-base leading-relaxed text-[var(--ob-ink-soft)]">{item.context}</p>
        </div>
        <figure className="ob-email-card overflow-hidden rounded-2xl border border-[var(--ob-line)] bg-white">
          <img
            src={item.img}
            alt={`${item.brand} verified Klaviyo before and after results`}
            loading="lazy"
            className="block w-full"
          />
        </figure>
      </article>
    </Reveal>
  );
}

function CampaignProof() {
  return (
    <Reveal>
      <div className="grid grid-cols-1 gap-8 rounded-3xl border border-[var(--ob-line)] bg-[var(--ob-dark)] p-6 md:grid-cols-[1fr_1.2fr] md:items-center md:p-10">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[var(--ob-accent)]">
            <ArrowUpRight size={16} /> Campaign Performance
          </span>
          <h3 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-white md:text-4xl">
            {CAMPAIGN_PROOF.headline}
          </h3>
          <p className="mt-4 text-base leading-relaxed text-white/60">{CAMPAIGN_PROOF.detail}</p>
        </div>
        <figure className="ob-email-card overflow-hidden rounded-2xl">
          <img
            src={CAMPAIGN_PROOF.img}
            alt="Klaviyo campaign performance, $77k from two campaigns"
            loading="lazy"
            className="block w-full"
          />
        </figure>
      </div>
    </Reveal>
  );
}

export default function CaseStudies() {
  return (
    <section id="results" className="bg-[var(--ob-bg-alt)] py-24 md:py-32">
      <div className="mx-auto max-w-[1280px] px-5 md:px-8">
        <Reveal className="mb-16 max-w-2xl">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ob-accent-dark)]">
            World-Class Klaviyo Management
          </p>
          <h2 className="font-display text-4xl font-extrabold tracking-tight text-[var(--ob-ink)] md:text-6xl">
            Don’t take our word for it. See the data.
          </h2>
          <p className="mt-5 text-lg text-[var(--ob-ink-soft)]">
            Real, verified Klaviyo dashboards from brands we’ve rebuilt from the ground up. Numbers you can actually read.
          </p>
        </Reveal>

        {/* Featured case study */}
        <FeaturedCase />

        {/* More verified results */}
        <Reveal className="mb-8 mt-20 max-w-2xl">
          <h3 className="font-display text-3xl font-extrabold tracking-tight text-[var(--ob-ink)] md:text-4xl">
            More verified results.
          </h3>
        </Reveal>
        <div className="space-y-6">
          {VERIFIED_RESULTS.map((item, i) => (
            <ResultBlock key={item.id} item={item} index={i} />
          ))}
        </div>

        {/* Campaign proof */}
        <div className="mt-6">
          <CampaignProof />
        </div>

        <Reveal className="mt-14 text-center">
          <a
            href="https://calendly.com/hello-onbrandadmin/30min" target="_blank" rel="noopener noreferrer"
            className="ob-btn group inline-flex items-center gap-2 rounded-full bg-[var(--ob-accent)] px-9 py-[1.15rem] text-lg font-bold text-[var(--ob-dark)] hover:bg-[var(--ob-accent-dark)] hover:text-white"
          >
            Get Results Like These
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </a>
        </Reveal>
      </div>
    </section>
  );
}
